import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { MESSAGE_KIND } from '../../../consts/message';
import { Count } from '../../../models/count';
import { MessageField } from 'Src/models/message';
import { ROLE } from 'Src/consts/user';

const DEFAULT_MESSAGES_FILTER: any = {
  allMessageTypes: true
};

const DEFAULT_FILTER: any = {
  limit: DEFAULT_LIMIT,
  skip: DEFAULT_SKIP,
  order: 'createdAt DESC'
};

export type MessageFolder = 'inbox' | 'outbox' | 'archive';

export interface MessageFolderResponse {
  items: any[];
  count: number;
  requiresFilters?: boolean;
}

export function getMessageFolder(
  user: AppUser,
  folder: MessageFolder,
  filter: any = DEFAULT_FILTER
): Promise<MessageFolderResponse> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/folders/${folder}`, {
      headers: { usid: key },
      params: {
        filter: {
          where: filter.where || {},
          skip: filter.skip,
          limit: filter.limit,
          order: filter.order,
          view: filter.view
        }
      }
    })
    .then(response => response.data);
}

export function getMessageFolderSelection(
  user: AppUser,
  folder: MessageFolder,
  where: any,
  total: number
): Promise<any[]> {
  return getMessageFolder(user, folder, {
    where: where || {},
    skip: 0,
    limit: Math.max(1, total || 1),
    view: { selectionOnly: true }
  }).then(result => result.items || []);
}

export function getMessageDetails(user: AppUser, messageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}`, {
      headers: { usid: key }
    })
    .then(response => response.data);
}

export function getInboxMessages(user: AppUser, filter = DEFAULT_MESSAGES_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_MESSAGES_FILTER,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/inbox`, config).then(response => {
    return response.data;
  });
}

export function getArchiveMessages(user: AppUser, filter = DEFAULT_MESSAGES_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_MESSAGES_FILTER,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/archive`, config).then(response => {
    return response.data;
  });
}

export function getOutboxMessages(user: AppUser, filter = DEFAULT_MESSAGES_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: { ...DEFAULT_MESSAGES_FILTER, ...filter.where },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/outbox`, config)
    .then(response => response.data);
}

export function getAllOutboxMessages(user: AppUser, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key },
    params: {
      filter: { where: { ...DEFAULT_MESSAGES_FILTER, ...(where || {}) }, limit: DEFAULT_LIMIT, skip: DEFAULT_SKIP }
    }
  };
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/outbox`, config)
    .then(response => response.data);
}

export function getOutboxMessagesCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key },
    params: { filter: { where: { ...DEFAULT_MESSAGES_FILTER, ...(where || {}) } } }
  };
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/outbox/count`, config)
    .then(response => response.data);
}

export function getAllInboxMessages(user: AppUser, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_MESSAGES_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_MESSAGES_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/inbox`, config).then(response => {
    return response.data;
  });
}

export function getAllArchiveMessages(user: AppUser, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_MESSAGES_FILTER,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_MESSAGES_FILTER
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/archive`, config).then(response => {
    return response.data;
  });
}

export function getInboxMessagesCount(user: AppUser, where?): Promise<Count> {
  return getMessageFolder(user, 'inbox', {
    where: where || {},
    view: { includeItems: false }
  }).then(result => ({ count: result.count }));
}

export function getArchiveMessagesCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_MESSAGES_FILTER,
            ...where
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_MESSAGES_FILTER
          }
        }
      }
    };
  }

  return axios
    .get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/archive/count`, config)
    .then(response => {
      return response.data;
    });
}

export function authorizeAbsentMessage(user: AppUser, messageId: string, authorizedComment?: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  let data;

  if (authorizedComment) {
    data = {
      isAuthorized: true,
      authorizedComment: authorizedComment
    };
  } else {
    data = {
      isAuthorized: true
    };
  }

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}/authorize`, data, config)
    .then(response => {
      return response.data;
    });
}

export function unAuthorizeAbsentMessage(user: AppUser, messageId: string, authorizedComment?: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  let data;

  if (authorizedComment) {
    data = {
      isAuthorized: false,
      authorizedComment: authorizedComment
    };
  } else {
    data = {
      isAuthorized: false
    };
  }

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}/authorize`, data, config)
    .then(response => {
      return response.data;
    });
}

export function getAvailabilityReports(user: AppUser, eventId, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          eventId: eventId,
          kind: MESSAGE_KIND.EventParticipationMessage
        },
        limit: DEFAULT_FILTER.limit,
        skip: DEFAULT_FILTER.skip,
        order: filter.order || DEFAULT_FILTER.order
      }
    }
  };
  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages`, config).then(response => {
    return response.data;
  });
}

export type AvailabilityWorkflowStatus = 'AVAILABLE' | 'PENDING' | 'EXPIRED' | 'NOT_AVAILABLE' | 'NOT_REQUESTED' | 'NO_RECIPIENT';
export interface EventAvailabilityWorkflowState {
  workflow: 'SELF_SERVICE' | 'MANUAL_REQUEST' | 'AUTO_ON_PLAYER_ADDED';
  parentEnabled: boolean;
  studentEnabled: boolean;
  responseEditingEnabled: boolean;
  summary: Record<AvailabilityWorkflowStatus, number>;
  items: Array<{
    player: { userId: string; permissionId: string; firstName?: string; lastName?: string; avatar?: string };
    status: AvailabilityWorkflowStatus;
    current?: any;
    history: any[];
    eligibleRecipients: {parent: number; student: number; total: number};
  }>;
}

export function getEventAvailabilityWorkflow(user: AppUser, eventId: string): Promise<EventAvailabilityWorkflowState> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/availability`, {headers: {usid: key}})
    .then(response => response.data);
}

export function requestEventAvailability(user: AppUser, eventId: string, data: {
  players?: Array<{userId: string; permissionId: string}>;
  recipients?: 'PARENT' | 'STUDENT' | 'BOTH';
  message?: string;
  deadline?: string;
  force?: boolean;
  pendingOnly?: boolean;
}): Promise<EventAvailabilityWorkflowState & Record<string, number>> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/availability/requests`, data, {headers: {usid: key}})
    .then(response => response.data);
}

export function getStudentAvailabilityReports(user: AppUser, eventId, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key } = user;
  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          eventId: eventId,
          kind: MESSAGE_KIND.EventParticipationMessage
        },
        limit: DEFAULT_FILTER.limit,
        skip: DEFAULT_FILTER.skip,
        order: filter.order || DEFAULT_FILTER.order
      }
    }
  };
  const path = user.activeRole === ROLE.PARENT
    ? `/i/children/events/${eventId}/messages/availability-history`
    : `/i/events/${eventId}/messages/availability-history`;
  return axios.get(`${window.apiBase}${path}`, config).then(response => {
    return response.data;
  });
}

export function getParentalConsent(user: AppUser, eventId) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          eventId: eventId,
          kind: MESSAGE_KIND.EventInvitationMessage
        },
        limit: DEFAULT_FILTER.limit,
        skip: DEFAULT_FILTER.skip
      }
    }
  };
  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages`, config).then(response => {
    return response.data;
  });
}

export function doGotItActionForEventMessage(user: AppUser, messageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}/gotit`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function updateStaffConsentStatus(user: AppUser, eventId: string, messageId: string, data) {
  return axios.post(
    `${window.apiBase}/i/schools/${user.activeSchoolId}/events/${eventId}/consent-requests/${messageId}/status`,
    data,
    { headers: { usid: user.roleSessionKey } }
  ).then(response => response.data);
}

export function rejectConfirmationRequest(user: AppUser, messageId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}/reject`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function acceptConfirmationRequest(
  user: AppUser,
  messageId: string,
  data: { fields?: MessageField[]; formSchemaVersion?: 2; formAnswers?: any[] } = {}
) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}/accept`, data, config)
    .then(response => {
      return response.data;
    });
}

export function setConfirmationRequestFields(user: AppUser, messageId: string, data: MessageField[]) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/${messageId}`, { fields: data }, config)
    .then(response => {
      return response.data;
    });
}

export function getInboxTournamentConfirmationRequests(user: AppUser, filter = DEFAULT_MESSAGES_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/tournamentConfirmation/inbox`, config)
    .then(response => {
      return response.data;
    });
}

export function getInboxTournamentConfirmationRequestsCount(user: AppUser, where?): Promise<number> {
  const { roleSessionKey: key, activeSchoolId } = user;
  let config;
  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: { ...where }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: { ...DEFAULT_MESSAGES_FILTER }
        }
      }
    };
  }
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/tournamentConfirmation/inbox/count`, config)
    .then(response => {
      const data = response.data as { count: number };
      return data.count;
    });
}

export function getArchiveTournamentConfirmationRequests(user: AppUser, filter = DEFAULT_MESSAGES_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/tournamentConfirmation/archive`, config)
    .then(response => {
      return response.data;
    });
}

export function getArchiveTournamentConfirmationRequestsCount(user: AppUser, where?): Promise<number> {
  const { roleSessionKey: key, activeSchoolId } = user;
  let config;
  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: { ...where }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: { ...DEFAULT_MESSAGES_FILTER }
        }
      }
    };
  }
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/messages/tournamentConfirmation/archive/count`, config)
    .then(response => {
      const data = response.data as { count: number };
      return data.count;
    });
}
